export interface StudentStory {
  id: number;
  name: string;
  grade: string;
  subject: string;
  avatar: string;
  beforeGrade: number;
  afterGrade: number;
  beforeLabel: string;
  afterLabel: string;
  weeksToImprove: number;
  quote: string;
  parentQuote?: string;
  parentName?: string;
  tags: string[];
  featured?: boolean;
}

export interface StatHighlight {
  value: string;
  label: string;
  icon: string;
  color: string;
}

export const statHighlights: StatHighlight[] = [
  { value: '94%', label: 'of students improved by at least one full letter grade', icon: 'ri-bar-chart-2-line', color: 'emerald' },
  { value: '2,400+', label: 'students helped since 2018', icon: 'ri-group-line', color: 'teal' },
  { value: '31 pts', label: 'average Regents score increase', icon: 'ri-trophy-line', color: 'amber' },
  { value: '4.9★', label: 'average parent & student rating', icon: 'ri-star-fill', color: 'rose' },
];

export const studentStories: StudentStory[] = [
  {
    id: 1,
    name: 'Marcus T.',
    grade: 'Grade 9 — Algebra I',
    subject: 'Algebra I',
    avatar: 'https://public.readdy.ai/ai/img_res/7a7e2f9367b0ea801d7f28a155c21626.jpg',
    beforeGrade: 58,
    afterGrade: 91,
    beforeLabel: 'F',
    afterLabel: 'A−',
    weeksToImprove: 10,
    quote: "I used to dread math class every single day. After just a few weeks with Mr. Augustine's lessons, I actually started looking forward to it. The way he breaks down each concept step by step made everything click.",
    parentQuote: "Marcus went from failing to earning a 91 on his Algebra Regents. We couldn't be more proud.",
    parentName: 'Sandra T. (Parent)',
    tags: ['Algebra I', 'Regents Prep', 'Grade 9'],
    featured: true,
  },
  {
    id: 2,
    name: 'Aaliyah R.',
    grade: 'Grade 10 — Geometry',
    subject: 'Geometry',
    avatar: 'https://readdy.ai/api/search-image?query=teenage%20girl%20student%20smiling%20portrait%20clean%20background%20school%20photo%20natural%20light%20warm%20tones&width=200&height=200&seq=ss2&orientation=squarish',
    beforeGrade: 62,
    afterGrade: 88,
    beforeLabel: 'D',
    afterLabel: 'B+',
    weeksToImprove: 8,
    quote: "Geometry proofs were my nightmare. The video lessons made me realize I was just missing a few foundational pieces. Once I filled those gaps, everything else fell into place.",
    tags: ['Geometry', 'Regents Prep', 'Grade 10'],
    featured: true,
  },
  {
    id: 3,
    name: 'Devon K.',
    grade: 'Grade 11 — Algebra 2',
    subject: 'Algebra 2',
    avatar: 'https://readdy.ai/api/search-image?query=teenage%20boy%20student%20smiling%20portrait%20clean%20background%20school%20photo%20natural%20light%20warm%20tones&width=200&height=200&seq=ss3&orientation=squarish',
    beforeGrade: 55,
    afterGrade: 84,
    beforeLabel: 'F',
    afterLabel: 'B',
    weeksToImprove: 12,
    quote: "Trig identities and logarithms had me completely lost. The structured lessons and practice tests here gave me a real roadmap. I passed my Algebra 2 Regents on the first try.",
    parentQuote: "Devon's confidence in math completely transformed. He now helps his younger sister with her homework!",
    parentName: 'James K. (Parent)',
    tags: ['Algebra 2', 'Regents Prep', 'Grade 11'],
    featured: true,
  },
  {
    id: 4,
    name: 'Sofia M.',
    grade: 'Grade 5 — Elementary Math',
    subject: 'Elementary Math',
    avatar: 'https://readdy.ai/api/search-image?query=young%20girl%20student%20smiling%20portrait%20clean%20background%20school%20photo%20natural%20light%20warm%20tones&width=200&height=200&seq=ss4&orientation=squarish',
    beforeGrade: 70,
    afterGrade: 96,
    beforeLabel: 'C−',
    afterLabel: 'A',
    weeksToImprove: 6,
    quote: "Fractions and decimals used to make me cry. Now I love math! The lessons are fun and I understand everything.",
    parentQuote: "Sofia's teacher called us to say she's now one of the top students in her class. Incredible turnaround.",
    parentName: 'Maria M. (Parent)',
    tags: ['Elementary', 'Grade 5', 'Fractions'],
  },
  {
    id: 5,
    name: 'Jaylen W.',
    grade: 'Grade 8 — Pre-Algebra',
    subject: 'Pre-Algebra',
    avatar: 'https://readdy.ai/api/search-image?query=teenage%20boy%20student%20smiling%20portrait%20clean%20background%20school%20photo%20natural%20light%20warm%20tones&width=200&height=200&seq=ss5&orientation=squarish',
    beforeGrade: 64,
    afterGrade: 89,
    beforeLabel: 'D',
    afterLabel: 'B+',
    weeksToImprove: 9,
    quote: "I was really behind going into 8th grade. The Grade 8 lessons helped me catch up fast. I scored in the top 20% on my state test.",
    tags: ['Grade 8', 'State Test', 'Pre-Algebra'],
  },
  {
    id: 6,
    name: 'Priya N.',
    grade: 'Grade 12 — Pre-Calculus',
    subject: 'Pre-Calculus',
    avatar: 'https://readdy.ai/api/search-image?query=teenage%20girl%20student%20smiling%20portrait%20clean%20background%20school%20photo%20natural%20light%20warm%20tones&width=200&height=200&seq=ss6&orientation=squarish',
    beforeGrade: 68,
    afterGrade: 93,
    beforeLabel: 'D+',
    afterLabel: 'A−',
    weeksToImprove: 14,
    quote: "I needed Pre-Calc to get into my dream college program. The lessons here were more thorough than anything my school offered. I ended the year with a 93 average.",
    parentQuote: "Priya got accepted into her first-choice engineering program. Math Mastery was a huge part of that.",
    parentName: 'Raj N. (Parent)',
    tags: ['Pre-Calculus', 'Grade 12', 'College Prep'],
  },
  {
    id: 7,
    name: 'Ethan B.',
    grade: 'Grade 9 — Algebra I',
    subject: 'Algebra I',
    avatar: 'https://readdy.ai/api/search-image?query=teenage%20boy%20student%20smiling%20portrait%20clean%20background%20school%20photo%20natural%20light%20warm%20tones&width=200&height=200&seq=ss7&orientation=squarish',
    beforeGrade: 72,
    afterGrade: 95,
    beforeLabel: 'C',
    afterLabel: 'A',
    weeksToImprove: 8,
    quote: "I was a C student who wanted to be an A student. The practice tests and detailed explanations made the difference. I scored a 95 on my Algebra Regents.",
    tags: ['Algebra I', 'Regents Prep', 'Grade 9'],
  },
  {
    id: 8,
    name: 'Zoe C.',
    grade: 'Grade 3 — Elementary Math',
    subject: 'Elementary Math',
    avatar: 'https://readdy.ai/api/search-image?query=young%20girl%20student%20smiling%20portrait%20clean%20background%20school%20photo%20natural%20light%20warm%20tones&width=200&height=200&seq=ss8&orientation=squarish',
    beforeGrade: 65,
    afterGrade: 92,
    beforeLabel: 'D+',
    afterLabel: 'A−',
    weeksToImprove: 7,
    quote: "I didn't like math before. Now it's my favorite subject! The lessons make it easy to understand.",
    parentQuote: "Zoe asks to do her math lessons every evening now. The transformation has been remarkable.",
    parentName: 'Claire C. (Parent)',
    tags: ['Elementary', 'Grade 3'],
  },
  {
    id: 9,
    name: 'Isaiah F.',
    grade: 'Grade 11 — Algebra 2',
    subject: 'Algebra 2',
    avatar: 'https://readdy.ai/api/search-image?query=teenage%20boy%20student%20smiling%20portrait%20clean%20background%20school%20photo%20natural%20light%20warm%20tones&width=200&height=200&seq=ss9&orientation=squarish',
    beforeGrade: 60,
    afterGrade: 86,
    beforeLabel: 'D−',
    afterLabel: 'B',
    weeksToImprove: 11,
    quote: "I failed Algebra 2 the first time. After using this program over the summer, I retook the Regents and passed with an 86. It changed everything for my senior year.",
    tags: ['Algebra 2', 'Regents Prep', 'Grade 11'],
  },
];

export const subjectFilters = [
  'All',
  'Elementary',
  'Grade 8',
  'Algebra I',
  'Geometry',
  'Algebra 2',
  'Pre-Calculus',
  'College Prep',
];
